import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Stack,
  Avatar,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { getInitials } from '../utils';
import { getMessageThread, sendMessage } from '../api/api';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';

const POLL_INTERVAL_MS = 10000;
// How close to the bottom (px) counts as "following" the conversation.
const STICK_THRESHOLD_PX = 80;

const formatTimestamp = (value) => {
  try {
    return new Date(value).toLocaleString([], {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch (err) {
    return '';
  }
};

const MessageThread = () => {
  const { t } = useTranslation();
  const { userId: otherUserId } = useParams();
  const navigate = useNavigate();
  const { user, getToken } = useKindeAuth();
  const { refresh: refreshUnreadCount } = useUnreadMessages();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [otherUser, setOtherUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const scrollRef = useRef(null);    // scrollable conversation container
  const bottomRef = useRef(null);    // sentinel at the end of the list
  const stickRef = useRef(true);     // was the user near the bottom before the last update?
  const lastSeenIdRef = useRef(null); // id of the newest message we have already reacted to

  const myId = user?.id;

  const isNearBottom = () => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD_PX;
  };

  const loadThread = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      // Capture scroll intent BEFORE the DOM updates with any new messages.
      stickRef.current = isNearBottom();
      const token = await getToken();
      const data = await getMessageThread(token, otherUserId);
      setOtherUser(data.otherUser);
      setMessages(data.messages || []);
      // The server marks this thread read when we fetch it, so refresh the
      // shared badge count immediately instead of waiting for its next poll.
      refreshUnreadCount();
    } catch (err) {
      if (!silent) setError(t('messages.loadError'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getToken, otherUserId, t, refreshUnreadCount]);

  useEffect(() => {
    loadThread();
    const interval = setInterval(() => loadThread({ silent: true }), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadThread]);

  // Only scroll when a genuinely new message arrives — never on a poll that
  // returns the same messages — and only if the user is following along (near
  // the bottom) or sent the message themselves.
  useEffect(() => {
    const newest = messages[messages.length - 1];
    const newestId = newest?._id;
    if (!newestId) return;
    if (newestId === lastSeenIdRef.current) return; // no new message → leave scroll alone

    const isInitial = lastSeenIdRef.current === null;
    const mine = newest.senderId === myId;
    lastSeenIdRef.current = newestId;

    if (isInitial || mine || stickRef.current) {
      bottomRef.current?.scrollIntoView({ block: 'end', behavior: isInitial ? 'auto' : 'smooth' });
    }
  }, [messages, myId]);

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const token = await getToken();
      const result = await sendMessage(token, otherUserId, body);
      stickRef.current = true; // always follow our own message
      setMessages((prev) => [...prev, result.message]);
      setDraft('');
    } catch (err) {
      setError(err?.response?.data?.error || t('messages.sendError'));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const otherName = otherUser
    ? `${otherUser.firstName || ''} ${otherUser.lastName || ''}`.trim()
    : '';

  const goToProfile = () => navigate(`/profile/${otherUserId}`);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxWidth: 900, mx: 'auto', width: '100%' }}>
      {/* Header — fixed */}
      <Paper elevation={0} sx={{ flexShrink: 0, borderBottom: '1px solid #e0e0e0', borderRadius: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ p: 1.5 }}>
          <IconButton onClick={() => navigate('/messages')} size="small">
            <ArrowBackIcon />
          </IconButton>
          {otherUser && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              onClick={goToProfile}
              sx={{ cursor: 'pointer', minWidth: 0, '&:hover': { opacity: 0.8 } }}
            >
              {otherUser.hasPicture ? (
                <Avatar src={otherUser.profilePictureUrl} alt={otherName} />
              ) : (
                <Avatar sx={{ flexShrink: 0 }}>{getInitials(otherName || '?')}</Avatar>
              )}
              <Typography variant="h6" noWrap sx={{ fontSize: '1.1rem', minWidth: 0 }}>{otherName}</Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Messages — the only scrollable region */}
      <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2, backgroundColor: '#fafafa' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
            {t('messages.emptyThread')}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {messages.map((m) => {
              const mine = m.senderId === myId;
              return (
                <Box
                  key={m._id || m.createdAt}
                  sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 1,
                      maxWidth: isMobile ? '80%' : '65%',
                      backgroundColor: mine ? '#1976d2' : '#ffffff',
                      color: mine ? '#ffffff' : 'text.primary',
                      border: mine ? 'none' : '1px solid #e0e0e0',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {m.body}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', textAlign: 'right', mt: 0.5, opacity: 0.7 }}
                    >
                      {formatTimestamp(m.createdAt)}
                    </Typography>
                  </Paper>
                </Box>
              );
            })}
            <div ref={bottomRef} />
          </Stack>
        )}
      </Box>

      {/* Composer — fixed */}
      <Paper elevation={0} sx={{ flexShrink: 0, borderTop: '1px solid #e0e0e0', borderRadius: 0, p: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder={t('messages.placeholder')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            inputProps={{ maxLength: 2000 }}
          />
          <IconButton color="primary" onClick={handleSend} disabled={sending || !draft.trim()}>
            <SendIcon />
          </IconButton>
        </Stack>
      </Paper>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageThread;
